import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ReportNav from '../report_v2/ReportNav';
import V4P1Portrait from './pages/V4P1Portrait';
import V4P2Overview from './pages/V4P2Overview';
import V4P3Strengths from './pages/V4P3Strengths';
import V4P4Weakness from './pages/V4P4Weakness';
import V4P5FinalEval from './pages/V4P5FinalEval';
import useReportTracking from '../../hooks/useReportTracking';

const HASH_PREFIX = '#/page/';

function parseHashIdx() {
  const h = window.location.hash || '';
  if (!h.startsWith(HASH_PREFIX)) return 0;
  const n = parseInt(h.slice(HASH_PREFIX.length), 10);
  return Number.isFinite(n) && n >= 1 ? n - 1 : 0;
}

const PAGES = [
  { id: 'v4-p1', title: '画像', kind: 'p1', pageType: 'overview' },
  { id: 'v4-p2', title: '概览', kind: 'p2', pageType: 'profile' },
  { id: 'v4-p3', title: '优势', kind: 'p3', pageType: 'strengths' },
  { id: 'v4-p4', title: '欠缺', kind: 'p4', pageType: 'weakness' },
  { id: 'v4-p5', title: '总评', kind: 'p5', pageType: 'final' },
];

/**
 * V4 报告主壳（M13）。
 *
 * 不自己 fetch — 由上层 ReportPageV2 / ReportPage.js 拿到 report_version === 4 的
 * payload 后传 props.report 进来；这样 polling / 错误处理 / 重试逻辑统一在 v2 主壳，
 * 不重复实现也避免 v2/v4 同时拉两次。
 */
export default function ReportPageV4({ report }) {
  const { assessmentId } = useParams();
  const [currentIdx, setCurrentIdx] = useState(() => parseHashIdx());
  const scrollerRef = useRef(null);
  const isProgrammaticScroll = useRef(false);

  const total = PAGES.length;

  // 当前页 → 埋点
  const currentPage = useMemo(() => {
    if (currentIdx < 0 || currentIdx >= PAGES.length) return null;
    const p = PAGES[currentIdx];
    return { pageType: p.pageType, dimId: null };
  }, [currentIdx]);

  const { reportComplete } = useReportTracking({
    assessmentId,
    currentPage,
    enabled: !!report,
  });

  // 末页到达 → report_complete
  useEffect(() => {
    if (currentPage?.pageType === 'final') reportComplete();
  }, [currentPage, reportComplete]);

  const goTo = useCallback((idx) => {
    const clamped = Math.max(0, Math.min(idx, total - 1));
    setCurrentIdx(clamped);
    const newHash = `${HASH_PREFIX}${clamped + 1}`;
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, '', newHash);
    }
    const el = scrollerRef.current;
    if (el) {
      isProgrammaticScroll.current = true;
      el.scrollTo({ left: clamped * el.clientWidth, behavior: 'smooth' });
    }
  }, [total]);

  useEffect(() => {
    const onHashChange = () => goTo(parseHashIdx());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(currentIdx - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goTo(currentIdx + 1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentIdx, goTo]);

  // initial scroll sync
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const initialIdx = Math.max(0, Math.min(currentIdx, total - 1));
    el.scrollTo({ left: initialIdx * el.clientWidth, behavior: 'auto' });
    if (initialIdx !== currentIdx) setCurrentIdx(initialIdx);
    // run once when v4 page mounts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync state when user scrolls
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (isProgrammaticScroll.current) {
          isProgrammaticScroll.current = false;
          return;
        }
        const w = el.clientWidth;
        if (w === 0) return;
        const idx = Math.round(el.scrollLeft / w);
        if (idx !== currentIdx) {
          const clamped = Math.max(0, Math.min(idx, total - 1));
          setCurrentIdx(clamped);
          const newHash = `${HASH_PREFIX}${clamped + 1}`;
          if (window.location.hash !== newHash) {
            window.history.replaceState(null, '', newHash);
          }
        }
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [currentIdx, total]);

  return (
    <div data-testid="report-page-v4-root" style={{ maxWidth: 880, margin: '0 auto' }}>
      <ReportNav pages={PAGES} currentIdx={currentIdx} onGoTo={goTo} />
      <div
        ref={scrollerRef}
        data-testid="report-page-v4-scroller"
        style={{
          display: 'flex',
          // ISSUE-017：默认 align-items=stretch 会让所有 section 被最高页（P4 错题列表）
          // 撑高，P5 出现大片空白；改 flex-start 让每页自然高度
          alignItems: 'flex-start',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
        }}
      >
        {PAGES.map((p) => (
          <section
            key={p.id}
            data-testid={`report-page-v4-slot-${p.id}`}
            style={{
              // ISSUE-017：flex item 默认 min-width: auto 会被内容撑大破坏 100% 宽，
              // 显式 width + minWidth=0 锁住每页严格 100% 父容器宽
              flex: '0 0 100%',
              width: '100%',
              minWidth: 0,
              scrollSnapAlign: 'start',
              padding: '24px 20px 48px',
              boxSizing: 'border-box',
            }}
          >
            {p.kind === 'p1' && <V4P1Portrait report={report} />}
            {p.kind === 'p2' && <V4P2Overview report={report} />}
            {p.kind === 'p3' && <V4P3Strengths report={report} />}
            {p.kind === 'p4' && <V4P4Weakness report={report} />}
            {p.kind === 'p5' && <V4P5FinalEval report={report} />}
          </section>
        ))}
      </div>
    </div>
  );
}
